<template>
  <section id="faq" class="faq-section">
    <h2 class="faq-section-title">{{ $t('faq.title') }}</h2>

    <div class="faq-content">
      <div
        v-for="category in visibleCategories"
        :key="category.key"
        class="faq-category"
      >
        <h3 class="faq-category-title">
          {{ $t('faq.' + category.key) }}
        </h3>

        <!-- All colours come from the token layer (useThemeTokens.js). The card
             shadows compose their own offsets (0 4px 6px / 0 8px 12px) from the
             colour-only --shadow-brand-color / --shadow-secondary-color. -->
        <div
          v-for="itemKey in category.items"
          :key="itemKey"
          class="faq-card"
        >
          <h4 class="faq-question-heading">
            <button
              type="button"
              class="faq-card-header"
              :aria-expanded="openItem === itemKey ? 'true' : 'false'"
              :aria-controls="`faq-answer-${itemKey}`"
              @click="toggle(itemKey)"
            >
              <span class="faq-question-text">{{ $t('faq.' + itemKey) }}</span>
              <span class="toggle-btn" :class="{ rotated: openItem === itemKey }" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="4,7 10,13 16,7" />
                </svg>
              </span>
            </button>
          </h4>

          <transition name="slide">
            <div v-if="openItem === itemKey" :id="`faq-answer-${itemKey}`" class="faq-answer-wrap">
              <p class="faq-answer">
                {{ $t('faq.' + answerKey(itemKey)) }}
              </p>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- The remaining categories stay collapsed until asked for -->
    <div class="faq-showall-wrap">
      <button
        type="button"
        class="faq-showall"
        :aria-expanded="showAllCategories ? 'true' : 'false'"
        @click="toggleAllCategories"
      >
        {{ showAllCategories ? $t('faq.showLess') : $t('faq.showAll', { count: totalQuestions }) }}
      </button>
    </div>

    <!-- CTA box. The panel is the CTA gradient; the inverted button inside it
         paints its text with the gradient's first stop (--cta-gradient-start). -->
    <div class="faq-cta">
      <h3 class="faq-cta-title">{{ $t('faq.ctaTitle') }}</h3>
      <p class="faq-cta-body">{{ $t('faq.ctaBody') }}</p>
      <ul class="faq-cta-bullets">
        <li>{{ $t('faq.ctaBullet1') }}</li>
        <li>{{ $t('faq.ctaBullet2') }}</li>
      </ul>
      <button
        type="button"
        class="faq-cta-btn"
        @pointerdown="prefetchBookingSheet"
        @click="openBookingSheet({ source: 'faq' })"
      >
        {{ $t('services.bookNow') }}
      </button>
    </div>
  </section>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBookingSheet } from '@/composables/useBookingSheet'

export default {
  name: 'FaqSection',
  setup() {
    const { t } = useI18n()
    const { open: openBookingSheet, prefetch: prefetchBookingSheet } = useBookingSheet()

    const openItem = ref(null)
    const showAllCategories = ref(false)

    const categories = [
      { key: 'cat1', items: ['q1'] },
      { key: 'cat2', items: ['q2', 'q3', 'q4'] },
      { key: 'cat3', items: ['q5', 'q6', 'q7'] },
      { key: 'cat4', items: ['q8'] },
      { key: 'cat5', items: ['q9', 'q10'] },
      { key: 'cat6', items: ['q11', 'q12', 'q13'] },
      { key: 'cat7', items: ['q14', 'q15'] },
    ]

    // Only the first category is rendered until the visitor asks for the rest.
    const visibleCategories = computed(() =>
      showAllCategories.value ? categories : categories.slice(0, 1)
    )

    const totalQuestions = categories.reduce((sum, cat) => sum + cat.items.length, 0)

    const toggleAllCategories = () => {
      showAllCategories.value = !showAllCategories.value
    }

    const toggle = (key) => {
      openItem.value = openItem.value === key ? null : key
    }

    // q1 → a1, q2 → a2, etc.
    const answerKey = (questionKey) => questionKey.replace('q', 'a')

    let scriptEl = null

    const buildJsonLd = () => {
      const mainEntity = []
      categories.forEach(cat => {
        cat.items.forEach(qKey => {
          mainEntity.push({
            '@type': 'Question',
            name: t('faq.' + qKey),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('faq.' + answerKey(qKey)),
            },
          })
        })
      })
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity,
      }
    }

    onMounted(() => {
      scriptEl = document.createElement('script')
      scriptEl.type = 'application/ld+json'
      scriptEl.id = 'faq-schema'
      scriptEl.textContent = JSON.stringify(buildJsonLd())
      document.head.appendChild(scriptEl)
    })

    onUnmounted(() => {
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl)
      }
    })

    return {
      categories,
      visibleCategories,
      totalQuestions,
      showAllCategories,
      toggleAllCategories,
      openItem,
      toggle,
      answerKey,
      openBookingSheet,
      prefetchBookingSheet,
    }
  },
}
</script>

<style scoped>
.faq-section {
  padding: clamp(48px, 8vw, 64px) var(--gutter);
  max-width: 900px;
  margin: 0 auto;
}

.faq-section-title {
  text-align: center;
  font-size: 2.5rem;
  color: var(--color-heading);
  text-shadow: 1px 1px 3px var(--text-shadow-color);
  margin-bottom: 3rem;
}

.faq-content {
  display: flex;
  flex-direction: column;
  gap: var(--s-6);
}

.faq-category-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--color-secondary);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(128, 128, 128, 0.2);
}

/* Card — same visual language as the service cards */
.faq-card {
  background: var(--surface-card);
  border-radius: 15px;
  /* Always transparent: the offerTheme slice hard-coded this literal, it was
     never a palette value, so P7 inlined it rather than minting a token. */
  border: 2px solid transparent;
  box-shadow: 0 4px 6px var(--shadow-brand-color);
  overflow: hidden;
  width: 100%;
  position: relative;
  transition: all 0.3s ease;
  margin-bottom: 0;
}

.faq-category {
  display: flex;
  flex-direction: column;
  gap: var(--s-3);
}

.faq-card:hover {
  box-shadow: 0 8px 12px var(--shadow-secondary-color);
  transform: translateY(-2px);
}

/* Question heading wraps a real disclosure button (keyboard + SR accessible) */
.faq-question-heading {
  margin: 0;
}

.faq-card-header {
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease;
  border-radius: 13px;
}

.faq-card-header:active {
  background: rgba(128, 128, 128, 0.1);
}

.faq-card-header:focus-visible,
.faq-showall:focus-visible,
.faq-cta-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* Question text — matches .offer-header h3 but one level smaller */
.faq-question-text {
  margin: 0;
  font-family: 'Playfair Display', serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-secondary);
  flex: 1;
  line-height: 1.4;
}

/* Circular gradient toggle affordance — the CTA gradient like every other
   gradient-painted button on the site */
.toggle-btn {
  display: flex;
  background: var(--gradient-cta);
  color: var(--text-on-cta);
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 20px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* The legacy hover was `linear-gradient(135deg, end 100%, start 0%)` — a
   degenerate gradient (the second stop is clamped to 100%) that paints solid
   brand.accent. Kept as exactly that; a cta-only end-stop token is P7's call. */
.toggle-btn:hover {
  /* The legacy hover was a degenerate gradient that rendered as the solid end
     stop, so this follows cta.gradientEnd rather than brand.accent. */
  background: var(--cta-gradient-end);
}

.toggle-btn.rotated {
  transform: rotate(180deg);
}

/* Answer body — matches .offer-description */
.faq-answer-wrap {
  padding: 0 20px 20px 20px;
}

/* Show all / show less toggle for categories 2..7 */
.faq-showall-wrap {
  display: flex;
  justify-content: center;
  margin-top: var(--s-5);
}

.faq-showall {
  min-height: 44px;
  padding: 0 var(--s-5);
  border: 2px solid var(--border-main);
  border-radius: 50px;
  background: transparent;
  color: var(--text-primary);
  font-family: 'Nunito', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.faq-showall:hover,
.faq-showall:active {
  background: var(--gradient-brand);
  color: var(--text-on-gradient);
  border-color: transparent;
}

.faq-answer {
  margin: 0;
  color: var(--text-on-card);
  font-size: 1.05rem;
  line-height: 1.8;
}

/* Slide transition for the answer body */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 600px;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.slide-enter-to,
.slide-leave-from {
  max-height: 600px;
  opacity: 1;
  transform: translateY(0);
}

/* CTA box — unchanged */
.faq-cta {
  margin-top: var(--s-6);
  padding: 2rem var(--s-5);
  border-radius: 18px;
  background: var(--gradient-cta);
  color: var(--text-on-cta);
  text-align: center;
}

.faq-cta-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.faq-cta-body {
  font-size: 1.05rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.faq-cta-bullets {
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1.5rem;
}

.faq-cta-bullets li {
  font-size: 0.95rem;
  font-weight: 600;
  opacity: 0.85;
}

.faq-cta-bullets li::before {
  content: '\2713\0020';
}

.faq-cta-btn {
  display: inline-block;
  min-height: 48px;
  border: none;
  cursor: pointer;
  padding: 14px 36px;
  background: var(--text-on-cta);
  color: var(--cta-gradient-start);
  border-radius: 50px;
  font-family: 'Nunito', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.03em;
  transition: opacity 0.2s ease, transform 0.15s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.faq-cta-btn:hover {
  opacity: 0.88;
}

.faq-cta-btn:active {
  transform: scale(0.97);
  opacity: 0.78;
}

/* Mobile */
@media (max-width: 768px) {
  .faq-section-title {
    font-size: 2rem;
  }

  .faq-category-title {
    font-size: 1.2rem;
  }

  .faq-question-text {
    font-size: 1.05rem;
  }

  .faq-answer {
    font-size: 0.95rem;
  }

  .faq-cta {
    padding: 2rem 1.25rem;
  }

  .faq-cta-title {
    font-size: 1.5rem;
  }

  .faq-cta-bullets {
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
  }
}

/* Disable sticky hover on touch devices */
@media (hover: none) {
  .faq-card:hover {
    box-shadow: 0 4px 6px var(--shadow-brand-color);
    transform: none;
  }
}

/* Desktop polish layer — additive only, does not touch base/mobile styles.
   .faq-cta is a sibling of .faq-content (outside the grid), so it stays
   full-width below the two-column grid automatically. */
@media (min-width: 1024px) {
  .faq-section-title {
    font-size: 3rem;
  }

  .faq-section {
    max-width: 1200px;
  }

  .faq-content {
    display: block;
    columns: 2;
    column-gap: 24px;
  }

  .faq-category {
    break-inside: avoid;
    margin-bottom: 24px;
  }
}
</style>
